import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import styled from '@emotion/styled';
import { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSpring, animated } from '@react-spring/web';
import { getOffset } from '../../utils/getOffset.es.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
const Preview = ({
  open,
  onClose,
  mousePosition,
  src
}) => {
  const ref = useRef();
  const [rendered, setRendered] = useState(open);
  const [showMask, setShowMask] = useState(open);
  const [showContent, setShowContent] = useState(open);
  const [previewPosition, setPreviewPosition] = useState();
  const [prevBodyAttrs, setPrevBodyAttrs] = useState({});
  const [props, api] = useSpring(
    () => ({
      from: { opacity: open ? 0 : 1 },
      to: { opacity: open ? 1 : 0 },
      config: {
        duration: 100
      },
      onResolve: () => {
        if (!open)
          setShowMask(false);
      }
    }),
    [open]
  );
  const [imgProps, imgApi] = useSpring(() => {
    const baseScale = typeof (previewPosition == null ? void 0 : previewPosition.width) === "number" ? mousePosition.width / (previewPosition == null ? void 0 : previewPosition.width) : 1;
    const baseTranslateX = typeof (previewPosition == null ? void 0 : previewPosition.left) === "number" ? mousePosition.left - (previewPosition == null ? void 0 : previewPosition.left) : 0;
    const baseTranslateY = typeof (previewPosition == null ? void 0 : previewPosition.top) === "number" ? mousePosition.top - (previewPosition == null ? void 0 : previewPosition.top) : 0;
    const scaleFrom = open ? baseScale : 1;
    const translateFrom = open ? `translate(${baseTranslateX}px, ${baseTranslateY}px)` : `translate(0px, 0px)`;
    const scaleTo = open ? 1 : baseScale;
    const translateTo = open ? `translate(0px, 0px)` : `translate(${baseTranslateX}px, ${baseTranslateY}px)`;
    return {
      from: { transform: translateFrom, scale: scaleFrom },
      to: { transform: translateTo, scale: scaleTo },
      config: {
        duration: 100
      },
      onResolve: () => {
        if (!open)
          setShowContent(false);
      }
    };
  }, [open, previewPosition]);
  const getParentWindow = () => {
    var _a, _b;
    return ((_b = (_a = ref.current) == null ? void 0 : _a.ownerDocument) == null ? void 0 : _b.defaultView) || window;
  };
  const getParentDocument = () => {
    var _a;
    return ((_a = ref.current) == null ? void 0 : _a.ownerDocument) || document;
  };
  const bodyScrollDisable = () => {
    setPrevBodyAttrs({
      overflow: getParentDocument().body.style.overflow,
      width: getParentDocument().body.style.width
    });
    const clientWidth = getParentDocument().body.clientWidth;
    getParentDocument().body.style.overflow = "hidden";
    getParentDocument().body.style.width = `${clientWidth}px`;
  };
  const bodyScrollEnable = () => {
    getParentDocument().body.style.width = prevBodyAttrs.width;
    getParentDocument().body.style.overflow = prevBodyAttrs.overflow;
    setPrevBodyAttrs({});
  };
  const handleClosePreview = () => {
    onClose == null ? void 0 : onClose();
  };
  useEffect(() => {
    api.start();
    imgApi.start();
  }, [open]);
  useEffect(() => {
    if (open) {
      setShowMask(true);
      setShowContent(true);
    }
  }, [open]);
  useEffect(() => {
    if (open && !rendered) {
      setRendered(true);
    }
  }, [open, rendered]);
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" || e.keyCode === 27) {
        e.preventDefault();
        e.stopPropagation();
        handleClosePreview();
      }
    };
    if (open && rendered) {
      bodyScrollDisable();
      getParentWindow().addEventListener("wheel", handleClosePreview);
      getParentWindow().addEventListener("touchstart", handleClosePreview);
      getParentDocument().addEventListener("keydown", handleKeyDown, false);
    } else {
      bodyScrollEnable();
      getParentWindow().removeEventListener("wheel", handleClosePreview);
      getParentWindow().removeEventListener("touchstart", handleClosePreview);
      getParentDocument().removeEventListener("keydown", handleKeyDown, false);
    }
    return () => {
      getParentWindow().removeEventListener("wheel", handleClosePreview);
      getParentWindow().removeEventListener("touchstart", handleClosePreview);
      getParentDocument().removeEventListener("keydown", handleKeyDown, false);
    };
  }, [open, rendered]);
  return rendered ? /* @__PURE__ */ jsxs(Fragment, {
    children: [
      /* @__PURE__ */ jsx("div", {
        ref,
        style: { display: "none" }
      }),
      createPortal(
        /* @__PURE__ */ jsxs("div", {
          children: [
            showMask && /* @__PURE__ */ jsx(MaskSpring, {
              style: props
            }),
            /* @__PURE__ */ jsxs(Content, {
              onClick: onClose,
              style: showContent ? {} : { display: "none" },
              children: [
                /* @__PURE__ */ jsx(StyledButtonUnzoom, {
                  type: "button",
                  children: /* @__PURE__ */ jsx("svg", {
                    "aria-hidden": "true",
                    "data-rmiz-btn-unzoom-icon": true,
                    fill: "currentColor",
                    focusable: "false",
                    viewBox: "0 0 16 16",
                    xmlns: "http://www.w3.org/2000/svg",
                    children: /* @__PURE__ */ jsx("path", {
                      d: "M 14.144531 1.148438 L 9 6.292969 L 9 3 L 8 3 L 8 8 L 13 8 L 13 7 L 9.707031 7 L 14.855469 1.851563 Z M 8 8 L 3 8 L 3 9 L 6.292969 9 L 1.148438 14.144531 L 1.851563 14.855469 L 7 9.707031 L 7 13 L 8 13 Z"
                    })
                  })
                }),
                /* @__PURE__ */ jsx(ImageSpring, {
                  style: __spreadProps(__spreadValues({}, imgProps), { transformOrigin: "top left" }),
                  src,
                  onLoad: (e) => {
                    setPreviewPosition(__spreadValues({
                      width: e.currentTarget.offsetWidth
                    }, getOffset(e.currentTarget)));
                  }
                })
              ]
            })
          ]
        }),
        getParentDocument().body
      )
    ]
  }) : null;
};
const Mask = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  height: 100%;
  background-color: rgba(255, 255, 255, 1);
  pointer-events: none;
`;
const MaskSpring = animated(Mask);
const Content = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10001;
  overflow: auto;
  outline: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Image = styled.img`
  max-width: 100vw;
  max-height: 100vh;
  cursor: zoom-out;
`;
const ImageSpring = animated(Image);
const StyledButtonUnzoom = styled.button`
  position: absolute;
  cursor: pointer;
  inset: 20px 20px auto auto;
  z-index: 1;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  border: none;
  box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
  color: #fff;
  height: 40px;
  margin: 0;
  outline-offset: 2px;
  padding: 9px;
  touch-action: manipulation;
  width: 40px;
`;

export { Preview };
